import { Minus, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { ColorSwatchPicker as AkunaColorSwatchPicker } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { ENTITY_COLORS, colorDotClass } from "./colors";

export type ModuleTone = "daycare" | "overnight" | "hotel";

const TONE_BADGE: Record<ModuleTone, string> = {
  daycare: "bg-orange-50 text-orange-600",
  overnight: "bg-purple-50 text-purple-700",
  hotel: "bg-purple-50 text-purple-700",
};

export function ModuleBadge({ tone, children }: { tone: ModuleTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-[11px] py-1 text-xs font-semibold",
        TONE_BADGE[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * The header every section on this tab shares: module badge, title, an inline
 * hint, an optional action, and a paragraph of explanatory copy underneath.
 */
export function SectionHeader({
  title,
  hint,
  description,
  action,
}: {
  tone: ModuleTone;
  badge: string;
  title: string;
  hint?: string;
  description: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-3.5">
      <div className="mb-1.5 flex flex-wrap items-center gap-2.5">
        <h2 className="font-display text-[17px] font-bold text-foreground">{title}</h2>
        {hint ? <span className="text-[13px] text-muted-foreground">{hint}</span> : null}
        {action ? <div className="ml-auto">{action}</div> : null}
      </div>
      <p className="text-[13.5px] text-muted-foreground">{description}</p>
    </div>
  );
}

export function SectionCard({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <div
      className={cn(
        "rounded-[18px] border border-border-gray bg-card shadow-[0_2px_6px_hsl(var(--foreground)/0.04)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Minus / value / plus control used by every capacity read-out on this tab. */
export function Stepper({
  value,
  onChange,
  min = 0,
  max = 999,
  size = "md",
  decreaseLabel,
  increaseLabel,
  disabled,
}: {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  size?: "sm" | "md";
  decreaseLabel: string;
  increaseLabel: string;
  disabled?: boolean;
}) {
  const buttonClass = cn(
    "flex items-center justify-center rounded-lg border border-border-gray bg-card text-color-medium",
    "transition-colors hover:bg-background-app focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-40",
    size === "sm" ? "h-7 w-7" : "h-[30px] w-[30px] rounded-[9px]",
  );
  const iconClass = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <div className={cn("flex items-center", size === "sm" ? "gap-2.5" : "gap-2.5")}>
      <button
        type="button"
        className={buttonClass}
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={disabled || value <= min}
        aria-label={decreaseLabel}
      >
        <Minus className={iconClass} aria-hidden="true" />
      </button>
      <span
        className={cn(
          "text-center font-display text-xl font-extrabold tabular-nums text-foreground",
          size === "sm" ? "min-w-6" : "min-w-8",
        )}
        aria-live="polite"
      >
        {value}
      </span>
      <button
        type="button"
        className={buttonClass}
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={disabled || value >= max}
        aria-label={increaseLabel}
      >
        <Plus className={iconClass} aria-hidden="true" />
      </button>
    </div>
  );
}

/** Dashed "add another one of these" tile that closes each card grid. */
export function AddTile({
  onClick,
  icon,
  tone,
  title,
  children,
  minHeight,
}: {
  onClick: () => void;
  icon: ReactNode;
  tone: ModuleTone;
  title: string;
  children?: ReactNode;
  minHeight: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ minHeight }}
      className={cn(
        "flex w-full flex-col items-center justify-center gap-2.5 rounded-[18px] border-[1.5px] border-dashed",
        "border-foreground/[0.18] bg-cream-50 px-4 py-7 text-center transition-colors hover:bg-cream-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <span
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-xl",
          tone === "daycare" ? "bg-orange-50 text-akuna-orange" : "bg-purple-50 text-akuna-purple",
        )}
      >
        {icon}
      </span>
      <span className="font-display text-[15px] font-bold text-foreground">{title}</span>
      {children}
    </button>
  );
}

export function ColorSwatchPicker({
  value,
  onChange,
  label,
  className,
}: {
  value: string | null;
  onChange: (color: string) => void;
  label: string;
  className?: string;
}) {
  return <AkunaColorSwatchPicker
    value={value}
    onChange={onChange}
    label={label}
    className={className}
    options={ENTITY_COLORS.map((color) => ({ value: color, className: colorDotClass(color) }))}
  />;
}
